import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getHeaders } from "../../utils/requestHeaders";
import { API_HOST } from "../../utils/constants";
import { buildMutationHelper } from "../../utils/buildMutationHelper";
import { setUsers } from "../reducer/users";
import { defaultFromState, isActiveReload } from "../../utils/defaultFromState";

export const usersApi = createApi({
  reducerPath: "usersApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_HOST,
  }),

  endpoints: (builder) => ({
    usersList: buildMutationHelper(
      builder,
      ({
        page = undefined,
        itemsPerPage = undefined,
        search = undefined,
        sortBy = undefined,
        sortType = undefined,
        isActive = undefined,
        client = localStorage?.selectedClientId ?? undefined,
      } = {}) => {
        return {
          url: "/system_users",
          params: {
            page: page,
            itemsPerPage: itemsPerPage,
            search: search,
            sortBy,
            sortType,
            isActive: isActive,
            client: client,
          },
          method: "GET",
          headers: getHeaders(),
          defaultFromState: (getState, args) => {
            const {
              users: {
                page,
                itemsPerPage,
                search,
                sortBy,
                sortType,
                isActive,
                client,
              },
            } = getState();

            let stateParams = {
              page,
              itemsPerPage,
              search,
              sortBy,
              sortType,
              isActive,
              client,
            };

            return defaultFromState(stateParams, args);
          },
        };
      },
      (response, dispatch) => {
        const { data } = response?.data;
        const totalCount = response?.data?.totalItems;
        dispatch(
          setUsers({
            data,
            totalCount,
            errorMessage: "",
          })
        );
      },
      ({ error }, dispatch) => {
        dispatch(setUsers({ errorMessage: error?.data?.error }));
      }
    ),
    userAdd: buildMutationHelper(
      builder,
      ({ usersData }) => ({
        url: `/user`,
        method: "POST",
        body: usersData,
        headers: getHeaders(),
      }),
      (response, dispatch) => {
        const { data } = response?.data;
        const totalCount = response?.data?.totalItems;
        dispatch(
          setUsers({
            data,
            totalCount,
            errorMessage: "",
          })
        );
      },
      ({ error }, dispatch) => {
        dispatch(
          setUsers({
            errorMessage: error?.data?.violations,
          })
        );
      }
    ),
    getUsers: buildMutationHelper(
      builder,
      (id) => ({
        url: `user/${id}`,
        method: "GET",
        headers: getHeaders(),
      }),
      (response, dispatch) => {
        const { data } = response?.data;
        dispatch(
          setUsers({
            userData: data,
            errorMessage: "",
          })
        );
      },
      ({ error }, dispatch) => {
        dispatch(setUsers({ errorMessage: error?.data?.error }));
      }
    ),
    editUsers: buildMutationHelper(
      builder,
      ({ usersData, id }) => ({
        url: `/user/${id}`,
        method: "PATCH",
        body: JSON.stringify(usersData),
        headers: getHeaders({
          customHeaders: {
            "Content-Type": "application/merge-patch+json",
            Accept: "application/ld+json",
          },
        }),
      }),
      (response, dispatch) => {
        const { data } = response?.data;
        const totalCount = response?.data?.totalItems;
        dispatch(
          setUsers({
            data,
            totalCount,
            errorMessage: "",
          })
        );
      },
      ({ error }, dispatch) => {
        dispatch(setUsers({ errorMessage: error?.data?.violations }));
      }
    ),
    activeUsers: buildMutationHelper(
      builder,
      ({ usersData, id }) => ({
        url: `/active-user/${id}`,
        method: "PATCH",
        body: JSON.stringify(usersData),
        headers: getHeaders({
          customHeaders: {
            "Content-Type": "application/merge-patch+json",
            Accept: "application/ld+json",
          },
        }),
      }),
      (response, dispatch, getState) => {
        const {
          endUser: { data: newData },
        } = getState();

        const { data } = response?.data;

        let newDataArray = [];
        isActiveReload(newDataArray, newData, data);

        const totalCount = response?.data?.totalItems;
        dispatch(
          setUsers({
            newDataArray,
            totalCount,
            errorMessage: "",
          })
        );
      },
      ({ error }, dispatch) => {
        dispatch(setUsers({ errorMessage: error?.data?.violations }));
      }
    ),
    getUsersPhoto: buildMutationHelper(
      builder,
      (id) => ({
        url: `/user-image/${id}`,
        method: "GET",
        headers: getHeaders(),
      }),
      (response, dispatch) => {
        const { data } = response?.data;
        dispatch(
          setUsers({
            usersData: data,
            errorMessage: "",
          })
        );
      },
      ({ error }, dispatch) => {
        dispatch(setUsers({ errorMessage: error?.data?.error }));
      }
    ),
    uploadAddUsersPhoto: buildMutationHelper(
      builder,
      ({ formData, id }) => ({
        url: `/user-image/${id}`,
        method: "POST",
        body: formData,
        headers: getHeaders({
          onlyFileUpload: true,
        }),
      }),
      (response, dispatch) => {
        const { data } = response?.data;

        dispatch(setUsers({ data, errorMessage: "" }));
      },
      ({ error }, dispatch) => {
        dispatch(setUsers({ errorMessage: error?.data?.error }));
      }
    ),
    deleteUser: buildMutationHelper(
      builder,
      (id) => ({
        url: `/user/delete/${id}`,
        method: "DELETE",
        headers: getHeaders(),
      }),
      ({ error }, dispatch) => {
        dispatch(setUsers({ errorMessage: error?.data?.error }));
      }
    ),
  }),
});
export const {
  useUsersListMutation,
  useUserAddMutation,
  useGetUsersMutation,
  useEditUsersMutation,
  useActiveUsersMutation,
  useGetUsersPhotoMutation,
  useUploadAddUsersPhotoMutation,
  useDeleteUserMutation,
} = usersApi;
